/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

/**
 *
 * @author Sebastian Javier Marchano (sebasjm)
 */

import {
  assertUnreachable,
  HttpStatusCode,
  parsePaytoUri,
  Paytos,
  PaytoType,
  PaytoUri,
  Result,
  TalerMerchantApi,
} from "@gnu-taler/taler-util";
import {
  LocalNotificationBannerBulma,
  useLocalNotificationBetter,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, h, VNode } from "preact";
import { StateUpdater, useState } from "preact/hooks";
import { ConfirmModal } from "../../../../components/modal/index.js";
import { useSessionContext } from "../../../../context/session.js";
import { Tooltip } from "../../../../components/Tooltip.js";

const TALER_SCREEN_ID = 35;

type Entity = TalerMerchantApi.BankAccountEntry;

interface Props {
  accounts: Entity[];
  onSelect: (e: Entity) => void;
  onCreate: () => void;
}

export function CardTable({ accounts, onCreate, onSelect }: Props): VNode {
  const [rowSelection, rowSelectionHandler] = useState<string[]>([]);

  const { i18n } = useTranslationContext();
  const { state: session, lib } = useSessionContext();
  const [notification, safeFunctionHandler] = useLocalNotificationBetter();
  const [deleting, setDeleting] =
    useState<TalerMerchantApi.BankAccountEntry | null>(null);

  const remove = safeFunctionHandler(
    i18n.str`delete bank account`,
    lib.instance.deleteBankAccount.bind(lib.instance),
    !session.token || !deleting ? undefined : [session.token, deleting.h_wire],
  );
  remove.onFail = (fail) => {
    switch (fail.case) {
      case HttpStatusCode.Unauthorized:
        return i18n.str`Unauthorized`;
      case HttpStatusCode.NotFound:
        return i18n.str`Not found`;
      default: {
        assertUnreachable(fail);
      }
    }
  };
  remove.onSuccess = () => {
    setDeleting(null);
    return i18n.str`The bank account has been deleted.`;
  };

  return (
    <Fragment>
      <LocalNotificationBannerBulma notification={notification} />

      {deleting && (
        <ConfirmModal
          label={i18n.str`Delete account`}
          description={i18n.str`Delete the account "${
            Result.orElse(Paytos.fromString(deleting.payto_uri), {
              displayName: i18n.str`Invalid payto: "${deleting.payto_uri}"`,
            }).displayName
          }"`}
          danger
          active
          onCancel={() => setDeleting(null)}
          confirm={remove}
        >
          <p>
            <i18n.Translate>
              If you delete the account with name{" "}
              <b>
                &quot;
                {
                  Result.orElse(Paytos.fromString(deleting.payto_uri), {
                    displayName: i18n.str`Invalid payto: "${deleting.payto_uri}"`,
                  }).displayName
                }
                &quot;
              </b>{" "}
              its information will be lost
            </i18n.Translate>
          </p>
          <p class="warning">
            <i18n.Translate>
              Deleting an account can't be undone.
            </i18n.Translate>
          </p>
        </ConfirmModal>
      )}

      <div class="card has-table">
        <header class="card-header">
          <p class="card-header-title">
            <i class="icon mdi mdi-bank" />
            <i18n.Translate>Bank accounts</i18n.Translate>
          </p>
          <div class="card-header-icon" aria-label="more options">
            <Tooltip text={i18n.str`Add new account`}>
              <button
                class="button is-info"
                type="button"
                accessKey="+"
                onClick={onCreate}
              >
                <i class="icon mdi mdi-plus mdi-36px" />
              </button>
            </Tooltip>
          </div>
        </header>
        <div class="card-content">
          <div class="b-table has-pagination">
            <div class="table-wrapper has-mobile-cards">
              {accounts.length > 0 ? (
                <Table
                  accounts={accounts}
                  onDelete={setDeleting}
                  onSelect={onSelect}
                  rowSelection={rowSelection}
                  rowSelectionHandler={rowSelectionHandler}
                />
              ) : (
                <EmptyTable />
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
interface TableProps {
  rowSelection: string[];
  accounts: Entity[];
  onDelete: (e: Entity) => void;
  onSelect: (e: Entity) => void;
  rowSelectionHandler: StateUpdater<string[]>;
}

function Table({ accounts, onDelete, onSelect }: TableProps): VNode {
  const { i18n } = useTranslationContext();
  const emptyList: Record<
    PaytoType | "unknown",
    { parsed: PaytoUri; acc: Entity }[]
  > = {
    bitcoin: [],
    "x-taler-bank": [],
    iban: [],
    "taler-reserve": [],
    "taler-reserve-http": [],
    unknown: [],
    ethereum: [],
    cyclos: [],
  };

  const accountsByType = accounts.reduce((prev, acc) => {
    const parsed = parsePaytoUri(acc.payto_uri);
    if (!parsed) return prev; //skip
    if (
      parsed.targetType !== "bitcoin" &&
      parsed.targetType !== "taler-reserve" &&
      parsed.targetType !== "taler-reserve-http" &&
      parsed.targetType !== "x-taler-bank" &&
      parsed.targetType !== "iban"
    ) {
      prev["unknown"].push({ parsed, acc });
    } else {
      prev[parsed.targetType].push({ parsed, acc });
    }
    return prev;
  }, emptyList);

  const bitcoinAccounts = accountsByType["bitcoin"];
  const talerbankAccounts = accountsByType["x-taler-bank"];
  const ibanAccounts = accountsByType["iban"];
  const unknownAccounts = accountsByType["unknown"];
  //FIXME: add support for payto://taler-reserve and payto://taler-reserve-http
  //FIXME: this code doesn't adapt well on new payto definitions
  return (
    <Fragment>
      <div class="table-container">
        <table class="table is-fullwidth is-striped is-hoverable is-fullwidth">
          <thead>
            <tr>
              <th>
                <i18n.Translate>Wire method</i18n.Translate>
              </th>
              <th>
                <i18n.Translate>Account</i18n.Translate>
              </th>
              <th>
                <i18n.Translate>Owner's name</i18n.Translate>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {bitcoinAccounts.map(({ parsed, acc }, idx) => {
              return (
                <tr key={idx}>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    Bitcoin
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {
                      Result.orElse(Paytos.fromString(acc.payto_uri), {
                        displayName: i18n.str`Invalid payto: "${acc.payto_uri}"`,
                      }).displayName
                    }
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {parsed.params["receiver-name"]}
                  </td>
                  <td class="is-actions-cell right-sticky">
                    <div class="buttons is-right">
                      <Tooltip
                        text={i18n.str`Delete selected accounts from the database`}
                      >
                        <button
                          class="button is-danger is-small"
                          onClick={() => onDelete(acc)}
                        >
                          <i18n.Translate>Delete</i18n.Translate>
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
            {talerbankAccounts.map(({ parsed, acc }, idx) => {
              return (
                <tr key={idx}>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    x-taler-bank
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {
                      Result.orElse(Paytos.fromString(acc.payto_uri), {
                        displayName: i18n.str`Invalid payto: "${acc.payto_uri}"`,
                      }).displayName
                    }
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {parsed.params["receiver-name"]}
                  </td>
                  <td class="is-actions-cell right-sticky">
                    <div class="buttons is-right">
                      <Tooltip
                        text={i18n.str`Delete selected accounts from the database`}
                      >
                        <button
                          class="button is-danger is-small"
                          onClick={() => onDelete(acc)}
                        >
                          <i18n.Translate>Delete</i18n.Translate>
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
            {ibanAccounts.map(({ parsed, acc }, idx) => {
              return (
                <tr key={idx}>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    IBAN
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {
                      Result.orElse(Paytos.fromString(acc.payto_uri), {
                        displayName: i18n.str`Invalid payto: "${acc.payto_uri}"`,
                      }).displayName
                    }
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {parsed.params["receiver-name"]}
                  </td>
                  <td class="is-actions-cell right-sticky">
                    <div class="buttons is-right">
                      <Tooltip
                        text={i18n.str`Delete selected accounts from the database`}
                      >
                        <button
                          class="button is-danger is-small"
                          onClick={() => onDelete(acc)}
                        >
                          <i18n.Translate>Delete</i18n.Translate>
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
            {unknownAccounts.map(({ parsed, acc }, idx) => {
              return (
                <tr key={idx}>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {parsed.targetType}
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {
                      Result.orElse(Paytos.fromString(acc.payto_uri), {
                        displayName: i18n.str`Invalid payto: "${acc.payto_uri}"`,
                      }).displayName
                    }
                  </td>
                  <td
                    onClick={(): void => onSelect(acc)}
                    style={{ cursor: "pointer" }}
                  >
                    {parsed.params["receiver-name"]}
                  </td>
                  <td class="is-actions-cell right-sticky">
                    <div class="buttons is-right">
                      <Tooltip
                        text={i18n.str`Delete selected accounts from the database`}
                      >
                        <button
                          class="button is-danger is-small"
                          onClick={() => onDelete(acc)}
                        >
                          <i18n.Translate>Delete</i18n.Translate>
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
}

function EmptyTable(): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="content has-text-grey has-text-centered">
      <p>
        <span class="icon is-large">
          <i class="icon mdi mdi-magnify mdi-48px" />
        </span>
      </p>
      <p>
        <i18n.Translate>
          There are no accounts yet, add more pressing the + sign
        </i18n.Translate>
      </p>
    </div>
  );
}
